import { memo, type MouseEvent, type ReactNode } from 'react'
import { MessageCircleMore, Sparkles, FileText, Mic, ArrowRight } from 'lucide-react'
import { Menu } from '@highlight/ui'
import { useViewActions } from '../../view-atoms'
import { usePinnedDisclosure } from '../cell-preview-card'
import { MEETING_VIEW_ID } from '../meeting/meeting-view-id'
import { formatTimeAgo } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'

type SourceIntegration = NonNullable<StagedAction['sourceIntegration']>

const INTEGRATION_LABELS: Record<SourceIntegration, string> = {
  linear: 'Linear',
  slack: 'Slack',
  notion: 'Notion',
  cursor: 'Cursor',
  reminders: 'Reminders',
  gmail: 'Gmail',
  github: 'GitHub',
  google_calendar: 'Google Calendar',
  google_docs: 'Google Docs',
  outlook_calendar: 'Outlook Calendar',
  claude_code: 'Claude Code',
  terminal: 'Terminal',
}

interface SourceRowProps {
  icon: ReactNode
  label: string
  value: string
  onClick?: (e: MouseEvent) => void
}

function SourceRow({ icon, label, value, onClick }: SourceRowProps) {
  const content = (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-4">{icon}</span>
      <span className="flex min-w-0 flex-1 flex-col items-start">
        <span className="text-foreground-secondary type-ui-xs">{label}</span>
        <span className="text-foreground type-ui-sm block w-full truncate font-medium">{value}</span>
      </span>
    </>
  )

  // Static context rows (no navigation) sit outside the menu's keyboard order.
  if (!onClick) {
    return <div className="flex w-full items-center gap-2 px-3 py-2">{content}</div>
  }

  // Navigable rows get a trailing arrow to signal the click opens the source.
  return (
    <Menu.Item onClick={onClick}>
      {content}
      <ArrowRight className="text-foreground-secondary shrink-0" />
    </Menu.Item>
  )
}

export interface ActionSourceProps {
  action: StagedAction
  /**
   * Conversation created by a staged-action edit fallback. Used as the chat target
   * when the action itself carries no `conversationId`, so the edit chat stays
   * reachable from the source attribution.
   */
  editConversationId?: string | null
}

/**
 * Every origin an action can be attributed to, resolved once. Module-level
 * rather than inline so callers that only need to know *whether* an action
 * names a source — the hover card, deciding whether to draw its connector —
 * read the same answer this component renders.
 */
function resolveActionOrigins(action: StagedAction, editConversationId?: string | null) {
  const meetingRef = action.references?.find((r) => r.type === 'audioNote' && r.audioNoteId)

  // The action's one chat origin, or none. "From Chat" only applies to actions a
  // chat actually authored: the backend resolves `conversationId` into `conversation`
  // metadata on expanded reads, and `type === 'chat'` marks a genuine user-visible
  // chat — planner / scheduled-skill runs reuse conversationId under other types and
  // get no chat label. An edit-fallback chat is created by our own edit flow (always
  // a chat) but is too new for the action to carry its metadata — trust it as a
  // title-less chat until the refetched action names it, but only when the action has
  // no conversation of its own; an action with a non-chat conversationId (planner /
  // scheduled skill) keeps its non-chat provenance through an edit fallback.
  const chat =
    action.conversation?.type === 'chat' && action.conversationId
      ? { id: action.conversationId, title: action.conversation.title || undefined }
      : !action.conversationId && editConversationId
        ? { id: editConversationId, title: undefined }
        : undefined
  const chatLabel = chat && (chat.title ?? 'Chat')

  // A meeting note (in-app audio note) and an integration source document are distinct
  // origins that can both back the same action — keep them separate so the popover can
  // list each. An untitled meeting still gets a fallback title so its row (and the link to
  // the note) renders whenever a meeting backs the action.
  const meetingTitle = meetingRef ? meetingRef.title || 'Audio Note' : undefined
  const sourceDocTitle = action.sourceMetadata?.documentTitle
  const sourceDocUrl = action.sourceMetadata?.documentUrl
  // We can only navigate to an external source when it carries a deep link.
  const canOpenSourceDoc = !!sourceDocUrl
  // sourceIntegration is free-form on the API (e.g. 'Slack', 'Notion') — normalize casing.
  const integration = action.sourceIntegration?.toLowerCase() as SourceIntegration | undefined
  const integrationLabel = integration ? INTEGRATION_LABELS[integration] : undefined
  const originSkill = action.sourceMetadata?.originSkill
  // Last-resort attribution from the first reference, so integration-only provenance
  // (e.g. a Linear/Notion reference with no meeting, chat, source document, or skill) still
  // names a source rather than showing only a timestamp. Renders as static text — a generic
  // reference carries no link the popover could open. The legacy 'Chat Conversation'
  // reference title is skipped: chat attribution comes only from the conversation metadata
  // above, never from a bare reference title.
  const primaryRefTitle = action.references?.[0]?.title
  const primaryRefLabel = primaryRefTitle === 'Chat Conversation' ? undefined : primaryRefTitle

  // Inline pill priority: a meeting, then a source document we can actually open
  // externally, then chat, then any source document, then the planner skill, then the
  // first reference's title. A source with no external link to navigate to drops behind
  // chat — it still shows as static context in the popover, just not as the pill's
  // primary target — but always names the pill before the skill that drafted the action.
  // Meeting and chat both read their specific title (falling back to "Meeting Note" /
  // "Chat"); chat only appears for a genuine chat, which is also the one the popover can
  // offer a row for.
  const inlineLabel =
    meetingTitle ??
    (canOpenSourceDoc ? sourceDocTitle : undefined) ??
    chatLabel ??
    sourceDocTitle ??
    originSkill ??
    primaryRefLabel

  return {
    meetingRef,
    chat,
    meetingTitle,
    sourceDocTitle,
    sourceDocUrl,
    integrationLabel,
    originSkill,
    inlineLabel,
    hasPopover: !!meetingTitle || !!sourceDocTitle || !!chat || !!originSkill,
  }
}

/**
 * Whether the action names an origin at all, or only carries a timestamp.
 *
 * Takes `editConversationId` for the same reason the component does: an
 * edit-fallback chat *is* an origin, so a caller passing one to `ActionSource`
 * has to pass it here too or the two disagree about whether there's a label.
 */
export function hasActionSourceLabel(action: StagedAction, editConversationId?: string | null): boolean {
  return !!resolveActionOrigins(action, editConversationId).inlineLabel
}

/**
 * Renders "<source> · <time ago>" attribution for a staged action — the counterpart to
 * `ReminderSource`, and the same in every host (outbox rows, the hover card, the Action
 * View header) so the attribution can't drift between them. When origin metadata is
 * available (source document, originating chat, or planner skill) the label becomes a
 * menu trigger surfacing each, with the source document and chat as menu items that
 * navigate and close the menu on click.
 */
export const ActionSource = memo(function ActionSource({ action, editConversationId }: ActionSourceProps) {
  const { openView } = useViewActions()
  // Pinned, because one of this component's homes is the cell hover card: the
  // popup anchors outside the card's rect, which Base UI reads as "pointer
  // left" and would close the card — and this menu with it — mid-click. Outside
  // a card the pin context is a no-op, so this is just controlled open state.
  const [menuOpen, setMenuOpen] = usePinnedDisclosure()

  const {
    meetingRef,
    chat,
    meetingTitle,
    sourceDocTitle,
    sourceDocUrl,
    integrationLabel,
    originSkill,
    inlineLabel,
    hasPopover,
  } = resolveActionOrigins(action, editConversationId)

  const openMeeting = meetingRef
    ? (e: MouseEvent) => {
        e.stopPropagation()
        openView(
          MEETING_VIEW_ID,
          { mode: 'note', audioNoteId: meetingRef.audioNoteId!, title: meetingRef.title || 'Audio Note' },
          { sourceLabel: 'Action' },
        )
      }
    : undefined

  const openChat = chat
    ? (e: MouseEvent) => {
        e.stopPropagation()
        // Jump straight to the latest messages — opening a conversation should land
        // on its most recent activity, not the top of the history.
        openView('chat', { chatId: chat.id, instantScrollToBottom: true }, { sourceLabel: 'Action' })
      }
    : undefined

  // The popover holds the full titles. The meeting row links to the in-app note; the
  // source-doc row links to the external doc only when it carries a deep link, else static.
  const openExternalDoc = sourceDocUrl
    ? (e: MouseEvent) => {
        e.stopPropagation()
        window.open(sourceDocUrl, '_blank')
      }
    : undefined
  // Both halves type themselves rather than inheriting — the hosts vary (a Cell
  // is `text-sm text-foreground`, the hover card inherits the document's), and
  // inheriting rendered the time larger and brighter than the label it trails.
  // The pairing matches `ReminderSource`, so the two attributions read alike
  // wherever they sit beside each other.
  const timeMeta = (
    <span className="text-foreground-secondary type-ui-xs flex shrink-0 items-center gap-1 font-medium">
      {inlineLabel && <span>·</span>}
      <span>{formatTimeAgo(action.createdAt)}</span>
    </span>
  )

  if (!inlineLabel || !hasPopover) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
        {inlineLabel && (
          <div className="text-foreground-secondary type-ui-xs min-w-0 flex-1 truncate font-medium">{inlineLabel}</div>
        )}
        {timeMeta}
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
      <Menu open={menuOpen} onOpenChange={setMenuOpen}>
        <Menu.Trigger
          variant="secondary"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Source: ${inlineLabel}`}
          // Menu.Trigger ships button chrome by default — strip it back to an inline text link.
          className="text-foreground-secondary hover:text-foreground-secondary pointer-events-auto relative z-10 flex h-auto min-w-0 cursor-pointer items-center gap-0 rounded-none border-0 bg-transparent p-0 text-xs font-medium shadow-none hover:bg-transparent hover:shadow-none active:scale-100"
        >
          <div className="min-w-0 truncate underline decoration-transparent transition-[text-decoration-color] duration-150 hover:decoration-current">
            {inlineLabel}
          </div>
        </Menu.Trigger>
        <Menu.Content side="top" align="start">
          <div className="flex w-64 flex-col">
            {chat && (
              <SourceRow
                icon={<MessageCircleMore className="text-foreground size-4" />}
                label="Chat"
                value={chat.title ?? 'Open conversation'}
                onClick={openChat}
              />
            )}
            {meetingTitle && (
              <SourceRow
                icon={<Mic className="text-foreground size-4" />}
                label="Meeting"
                value={meetingTitle}
                onClick={openMeeting}
              />
            )}
            {sourceDocTitle && (
              <SourceRow
                icon={<FileText className="text-foreground size-4" />}
                label={integrationLabel ?? 'Source'}
                value={sourceDocTitle}
                onClick={openExternalDoc}
              />
            )}
            {originSkill && (
              <SourceRow icon={<Sparkles className="text-foreground size-4" />} label="Skill" value={originSkill} />
            )}
          </div>
        </Menu.Content>
      </Menu>
      {timeMeta}
    </div>
  )
})
